const AUTH_REFRESH_RATE_LIMIT_COOLDOWN_MS = 60_000;
const MAX_SCOPED_RATE_LIMITS = 200;

let globalAuthRefreshRateLimitUntilMs = 0;
const scopedAuthRefreshRateLimitUntilMs = new Map<string, number>();

type AuthRefreshRateLimitScopeSession = {
  accessToken?: string | null;
  refreshToken?: string | null;
} | null;

export { AUTH_REFRESH_RATE_LIMIT_COOLDOWN_MS };

export function resetAuthRefreshRateLimitForTests(): void {
  globalAuthRefreshRateLimitUntilMs = 0;
  scopedAuthRefreshRateLimitUntilMs.clear();
}

export function buildAuthRefreshRateLimitScope(
  session: AuthRefreshRateLimitScopeSession
): string | undefined {
  const token = session?.refreshToken || session?.accessToken;
  if (!token) return undefined;
  return `auth:${token.length}:${token.slice(0, 8)}:${token.slice(-8)}`;
}

function pruneExpiredScopedLimits(nowMs: number): void {
  for (const [scope, untilMs] of scopedAuthRefreshRateLimitUntilMs) {
    if (untilMs <= nowMs) {
      scopedAuthRefreshRateLimitUntilMs.delete(scope);
    }
  }

  if (scopedAuthRefreshRateLimitUntilMs.size <= MAX_SCOPED_RATE_LIMITS) {
    return;
  }

  const oldestScopes = [...scopedAuthRefreshRateLimitUntilMs.keys()]
    .slice(0, scopedAuthRefreshRateLimitUntilMs.size - MAX_SCOPED_RATE_LIMITS);
  for (const scope of oldestScopes) {
    scopedAuthRefreshRateLimitUntilMs.delete(scope);
  }
}

export function noteAuthRefreshRateLimited(
  scope?: string,
  nowMs = Date.now()
): number {
  const untilMs = nowMs + AUTH_REFRESH_RATE_LIMIT_COOLDOWN_MS;
  if (scope) {
    pruneExpiredScopedLimits(nowMs);
    scopedAuthRefreshRateLimitUntilMs.set(scope, untilMs);
  } else {
    globalAuthRefreshRateLimitUntilMs = untilMs;
  }
  return AUTH_REFRESH_RATE_LIMIT_COOLDOWN_MS;
}

export function getAuthRefreshRateLimitRemainingMs(
  scope?: string,
  nowMs = Date.now()
): number {
  const untilMs = scope
    ? scopedAuthRefreshRateLimitUntilMs.get(scope) ?? 0
    : globalAuthRefreshRateLimitUntilMs;

  const remainingMs = Math.max(0, untilMs - nowMs);
  if (remainingMs === 0 && scope) {
    scopedAuthRefreshRateLimitUntilMs.delete(scope);
  }
  return remainingMs;
}

function isRefreshTokenRequest(input: RequestInfo | URL): boolean {
  const rawUrl = typeof input === "string"
    ? input
    : input instanceof URL
      ? input.href
      : input.url;

  try {
    const url = new URL(rawUrl);
    return (
      url.pathname.endsWith("/auth/v1/token") &&
      url.searchParams.get("grant_type") === "refresh_token"
    );
  } catch {
    return false;
  }
}

function buildRetryableRefreshResponse(retryAfterMs: number): Response {
  return new Response(JSON.stringify({ msg: "Auth refresh is backing off" }), {
    status: 503,
    statusText: "Service Unavailable",
    headers: {
      "content-type": "application/json",
      "retry-after": String(Math.max(1, Math.ceil(retryAfterMs / 1000))),
    },
  });
}

export function createRateLimitAwareAuthFetch(scope?: string): typeof fetch {
  return async (input, init) => {
    if (!isRefreshTokenRequest(input)) {
      return fetch(input, init);
    }

    const remainingMs = getAuthRefreshRateLimitRemainingMs(scope);
    if (remainingMs > 0) {
      return buildRetryableRefreshResponse(remainingMs);
    }

    const response = await fetch(input, init);
    if (response.status !== 429) {
      return response;
    }

    const cooldownMs = noteAuthRefreshRateLimited(scope);
    return buildRetryableRefreshResponse(cooldownMs);
  };
}
